import React, { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Typography,
  Button, Alert, Chip, useMediaQuery, useTheme,
} from '@mui/material';
import { Close, ZoomIn } from '@mui/icons-material';

// Guía "paso a paso" con imágenes, en un Dialog. Contenido y lógica separados:
// quien lo usa pasa los pasos (ver GuiaActualizarPrecios.jsx).
//
// pasos: [{
//   corto,      // etiqueta breve para el índice de arriba
//   titulo,
//   texto,      // node
//   imagenes,   // [{ src, w, h, alt, leyenda?, ancho?, anchoZoom? }]
//               // w/h = tamaño real en px (reservan el alto mientras carga la imagen lazy, para que
//               // el índice de pasos aterrice bien); ancho/anchoZoom = anchos máximos en la guía y al ampliar
//   nota,       // { tipo: 'info' | 'warning', texto }
// }]
// Las imágenes se pueden ampliar tocándolas (en el celular se puede desplazar).

function Imagen({ img, onZoom }) {
  return (
    <Box sx={{ mt: 1.5, maxWidth: img.ancho || '100%' }}>
      <Box
        component="button"
        type="button"
        onClick={() => onZoom(img)}
        aria-label={`Ampliar imagen: ${img.alt}`}
        sx={{
          display: 'block', position: 'relative', width: '100%', p: 0, m: 0,
          border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden',
          bgcolor: '#fff', cursor: 'zoom-in', boxShadow: 1, font: 'inherit',
        }}
      >
        <Box
          component="img"
          src={img.src}
          alt={img.alt}
          width={img.w}
          height={img.h}
          loading="lazy"
          decoding="async"
          sx={{ display: 'block', width: '100%', height: 'auto' }}
        />
        <Box
          sx={{
            position: 'absolute', right: 8, top: 8, display: 'flex', alignItems: 'center',
            bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 1, px: 0.75, py: 0.25,
          }}
        >
          <ZoomIn fontSize="small" />
        </Box>
      </Box>
      {img.leyenda && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {img.leyenda}
        </Typography>
      )}
    </Box>
  );
}

function PasoAPaso({ open, onClose, titulo, intro, pasos = [], extra, accionFinal }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [zoom, setZoom] = useState(null);
  const [zoomAbierto, setZoomAbierto] = useState(false); // aparte de `zoom` para no vaciar la imagen durante la animación de cierre
  const refs = useRef([]);

  const irAlPaso = (i) => refs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        aria-labelledby="paso-a-paso-titulo"
      >
        <DialogTitle id="paso-a-paso-titulo" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          {titulo}
          <IconButton size="small" onClick={onClose} aria-label="Cerrar"><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {intro && <Box sx={{ mb: 2 }}>{intro}</Box>}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }} role="navigation" aria-label="Pasos">
            {pasos.map((p, i) => (
              <Chip
                key={i}
                size="small"
                clickable
                variant="outlined"
                color="primary"
                label={`${i + 1}. ${p.corto || p.titulo}`}
                onClick={() => irAlPaso(i)}
              />
            ))}
          </Box>

          {pasos.map((p, i) => (
            <Box
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              sx={{ display: 'flex', gap: 2, mb: 4, scrollMarginTop: 8 }}
            >
              <Box
                aria-hidden
                sx={{
                  flexShrink: 0, width: 36, height: 36, borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                  bgcolor: 'primary.main', color: 'primary.contrastText',
                }}
              >
                {i + 1}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                  {p.titulo}
                </Typography>
                <Typography variant="body1" component="div" color="text.secondary">
                  {p.texto}
                </Typography>
                {p.nota && (
                  <Alert severity={p.nota.tipo || 'info'} sx={{ mt: 1.5 }}>{p.nota.texto}</Alert>
                )}
                {(p.imagenes || []).map((img, k) => (
                  <Imagen key={k} img={img} onZoom={(i) => { setZoom(i); setZoomAbierto(true); }} />
                ))}
              </Box>
            </Box>
          ))}

          {extra}
        </DialogContent>
        <DialogActions>
          {accionFinal && (
            <Button variant="outlined" onClick={accionFinal.onClick}>{accionFinal.label}</Button>
          )}
          <Button variant="contained" onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={zoomAbierto}
        onClose={() => setZoomAbierto(false)}
        TransitionProps={{ onExited: () => setZoom(null) }}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 1 }}>
          <Typography variant="subtitle2" component="span" sx={{ fontWeight: 600 }}>{zoom?.alt}</Typography>
          <IconButton size="small" onClick={() => setZoomAbierto(false)} aria-label="Cerrar imagen"><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, overflow: 'auto', bgcolor: '#fff' }}>
          {zoom && (
            <Box
              component="img"
              src={zoom.src}
              alt={zoom.alt}
              sx={{ display: 'block', width: '100%', minWidth: isMobile ? Math.min(900, zoom.anchoZoom || 900) : 0, maxWidth: zoom.anchoZoom || 'none', mx: 'auto', height: 'auto' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PasoAPaso;
